// src/services/tables/DynastyTable.js - CORE TABLE COMPONENT WITH HEADER TOOLTIPS AND CUSTOM SORT

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { dynastyTheme } from '../colorService';
import { headerTooltips } from './tableHelpers';
import { formatBattingAvg, formatERA } from '../statFormatUtils';
import { getNestedValue } from '../../utils/getNestedValue';

export { getNestedValue };

// =============================================================================
// BASIC RENDER HELPERS - ALL REMOVE LEADING ZEROS FOR DECIMALS < 1
// =============================================================================
export const renderDefault = (v) => v || 0;

export const renderFloat1 = (v) => {
  const val = parseFloat(v) || 0;
  return val.toFixed(1);
};

export const renderFloat2 = (v) => {
  const val = parseFloat(v) || 0;
  return val.toFixed(2);
};

export const renderFloat3 = (v) => {
  if (v === null || v === undefined || v === '') return '.000';
  return formatBattingAvg(v);
};

export const renderAvg = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const val = parseFloat(v);
  if (isNaN(val)) return '-';
  if (val === 0) return '.000';
  return formatBattingAvg(v);
};

// =============================================================================
// TOOLTIP COMPONENT
// =============================================================================
const HeaderTooltip = ({ title, children, columnKey }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);

  const handleMouseEnter = (e) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Capture the rect NOW before the event becomes stale
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    // Show tooltip after 300ms delay
    timeoutRef.current = setTimeout(() => {
      // Calculate position to keep tooltip on screen
      let x = rect.left + rect.width / 2;
      
      // Adjust if tooltip would go off right edge
      if (x + 200 > viewportWidth) {
        x = viewportWidth - 210;
      }
      // Adjust if tooltip would go off left edge
      if (x - 100 < 10) {
        x = 110;
      }
      
      setTooltipPosition({
        x: x,
        y: rect.bottom + 5
      });
      setShowTooltip(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowTooltip(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Get tooltip text from mapping or use full title
  const tooltip = headerTooltips[title] || 
                  headerTooltips[columnKey] || 
                  (title.length > 5 ? title : null);
  
  // Don't show tooltip if there's no expansion needed
  const shouldShowTooltip = tooltip && tooltip !== title;

  return (
    <>
      <div
        ref={containerRef}
        onMouseEnter={shouldShowTooltip ? handleMouseEnter : undefined}
        onMouseLeave={shouldShowTooltip ? handleMouseLeave : undefined}
        className="inline-flex items-center"
      >
        {children}
      </div>
      
      {showTooltip && shouldShowTooltip && (
        <div
          ref={tooltipRef}
          className="fixed z-[100] pointer-events-none animate-fadeIn"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translateX(-50%)',
            maxWidth: '400px'
          }}
        >
          <div className={`
            px-3 py-2 rounded-md shadow-2xl
            ${dynastyTheme.classes.bg.darkFlat}
            ${dynastyTheme.classes.border.primary}
            border backdrop-blur-md bg-opacity-95
          `}>
            <div className={`text-xs ${dynastyTheme.classes.text.white} font-medium`}>
              {tooltip}
            </div>
            {title !== columnKey && (
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-1`}>
                Column: {columnKey}
              </div>
            )}
          </div>
          {/* Arrow pointer */}
          <div 
            className={`absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 
              ${dynastyTheme.classes.bg.darkFlat} ${dynastyTheme.classes.border.primary}
              border-l border-t rotate-45`}
          />
        </div>
      )}
    </>
  );
};

// =============================================================================
// MAIN TABLE COMPONENT - DYNASTY THEME INTEGRATED
// =============================================================================
export const DynastyTable = ({
  data = [],
  columns = [],
  initialSort = null,
  className = '',
  maxHeight = '600px',
  minWidth = '1000px',  // Reduced from 1400px
  stickyHeader = true,
  title = '',
  showTotals = false,
  totalsRow = null,
  enableHorizontalScroll = true,
  enableVerticalScroll = true,
  defaultCellPadding = 'py-1 px-1',
  titlePadding = 'py-3',      // Vertical padding for the title bar
  // NEW PROPS FOR TABLE TYPES AND RENDERING MODES
  tableType = 'player-list',  // 'roster-lineup' | 'player-list' | 'flat-data'
  leaguePositions = null,     // For roster-lineup: {hitting: [...], pitching: [...]}
  playerType = null,          // For roster-lineup: 'hitting' | 'pitching'
  rowsPerItem = 1,            // How many rows per item (1, 2, or 3)
  rowTypes = ['season'],      // ['season'] | ['accrued'] | ['season', 'rolling'] | etc.
  getRowClassName = null,     // Function: (rowData, index, prevRowData) => className string
  fillHeight = false,         // When true, table fills parent height (flex) instead of using maxHeight
  onRowClick = null,          // Callback: (rowData) => void — fires when a data row is clicked
  rowId = null                // Function: (rowData) => string|null — DOM id for the item's primary row.
                              // Used for notification deep-link anchors (Bible § 4.5.12.2). Return a
                              // falsy value to emit no id (no fabrication). Emitted only on the FIRST
                              // row of a multi-row item so the anchor is unique per item.
}) => {
  const [sortConfig, setSortConfig] = useState(initialSort);
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const tableRef = useRef(null);

  // =============================================================================
  // INTERNAL FUNCTION: Generate position slots for roster-lineup tables
  // =============================================================================
  const generatePositionSlots = useCallback((positionArray) => {
    if (!positionArray || !Array.isArray(positionArray)) return [];
    
    const slots = [];
    const positionCounts = {};
    
    positionArray.forEach(pos => {
      const count = positionCounts[pos] || 0;
      slots.push({
        slotPosition: pos,           // Display value (e.g., 'C')
        slotId: `${pos}_${count}`,  // Assignment value (e.g., 'C_0')
        player: null                 // Will be filled by matching logic
      });
      positionCounts[pos] = count + 1;
    });
    
    return slots;
  }, []);

  // Calculate default width for a column - ONLY defaults, never override state
  const getDefaultColumnWidth = useCallback((col) => {
    // Use explicit width if provided
    if (col.width) return parseInt(col.width);
    // Use minWidth if provided
    if (col.minWidth) return parseInt(col.minWidth);
    // Stat columns get smaller default
    if (col.isStatColumn) return 60;
    // Position/role columns
    if (col.key.includes('position') || col.key === 'pos') return 50;
    // Player name columns need more space
    if (col.key.includes('name') || col.key.includes('player')) return 180;
    // Default for other columns
    return 80;
  }, []);

  // Initialize column widths with smart defaults - ONLY ONCE on mount or column changes
  useEffect(() => {
    const defaultWidths = {};
    columns.forEach(col => {
      defaultWidths[col.key] = getDefaultColumnWidth(col);
    });
    setColumnWidths(defaultWidths);
  }, [columns, getDefaultColumnWidth]);

  // Get the actual width to use for a column - respects user resizing
  const getColumnWidth = useCallback((columnKey) => {
    // Always use state width if it exists (user may have resized)
    if (columnWidths[columnKey] !== undefined) {
      return columnWidths[columnKey];
    }
    // Fallback to finding the column and getting its default
    const col = columns.find(c => c.key === columnKey);
    return col ? getDefaultColumnWidth(col) : 80;
  }, [columnWidths, columns, getDefaultColumnWidth]);

  // Improved sorting with custom sortValue support
  const sortedData = useMemo(() => {
    if (!sortConfig || !data.length) return data;

    // Find the column configuration for custom sort values
    const column = columns.find(col => col.key === sortConfig.key);

    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      // Check if column has custom sortValue function
      if (column?.sortValue) {
        aValue = column.sortValue(a);
        bValue = column.sortValue(b);
      } else if (column?.accessor) {
        // Use accessor function if available (for proper date/number sorting)
        aValue = typeof column.accessor === 'function' ? column.accessor(a) : getNestedValue(a, column.accessor);
        bValue = typeof column.accessor === 'function' ? column.accessor(b) : getNestedValue(b, column.accessor);
      } else {
        // Use getNestedValue to support nested paths like 'season_stats.batting_avg'
        aValue = getNestedValue(a, sortConfig.key);
        bValue = getNestedValue(b, sortConfig.key);
      }

      // Early returns for performance
      if (aValue === bValue) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      const result = aStr.localeCompare(bStr);
      return sortConfig.direction === 'asc' ? result : -result;
    });
  }, [data, sortConfig, columns]);

  // Memoized callbacks
  const handleSort = useCallback((columnKey) => {
    setSortConfig(prevConfig => {
      if (prevConfig?.key === columnKey) {
        if (prevConfig.direction === 'asc') {
          return { key: columnKey, direction: 'desc' };
        } else if (prevConfig.direction === 'desc') {
          return null;
        }
      }
      return { key: columnKey, direction: 'asc' };
    });
  }, []);

  const getSortIcon = useCallback((columnKey) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return (
        <ChevronsUpDown 
          className={`w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity ${dynastyTheme.classes.text.neutralLight}`} 
        />
      );
    }
    
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className={`w-3 h-3 ${dynastyTheme.classes.text.primary}`} />
    ) : (
      <ChevronDown className={`w-3 h-3 ${dynastyTheme.classes.text.primary}`} />
    );
  }, [sortConfig]);

  const handleMouseDown = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    
    const startX = e.clientX;
    const startWidth = getColumnWidth(columnKey);

    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(25, startWidth + diff);
      setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [getColumnWidth]);

  // Memoized styles
  const overflowClass = useMemo(() => {
    const base = enableHorizontalScroll && enableVerticalScroll ? 'overflow-auto' :
                 enableHorizontalScroll ? 'overflow-x-auto overflow-y-hidden' :
                 enableVerticalScroll ? 'overflow-y-auto overflow-x-hidden' :
                 'overflow-hidden';
    return `${base} scrollbar-thin scrollbar-thumb-yellow-400 scrollbar-track-gray-800`;
  }, [enableHorizontalScroll, enableVerticalScroll]);

  const tableStyles = useMemo(() => ({
    minWidth: enableHorizontalScroll ? minWidth : 'auto'
  }), [enableHorizontalScroll, minWidth]);

  const containerStyles = useMemo(() => ({
    ...(fillHeight ? { flex: 1, minHeight: 0 } : { maxHeight }),
    scrollbarWidth: 'thin',
    scrollbarColor: '#ca8a04 transparent'
  }), [maxHeight, fillHeight]);

  return (
    <div className={`dynasty-table-wrapper ${className} relative`} style={fillHeight ? { display: 'flex', flexDirection: 'column', height: '100%' } : undefined}>
      {/* Animation for tooltip fade-in */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-5px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        /* Season row - white text */
        .dynasty-season-row {
          background: rgb(38, 38, 38) !important;
          color: white !important;
          transform: translateZ(0);
          will-change: transform;
          contain: layout style paint;
        }
        
        /* Rolling row - yellow text, italic */
        .dynasty-rolling-row {
          background: rgb(23, 23, 23) !important;
          color: #FBBF24 !important;
          font-style: italic;
          transform: translateZ(0);
          will-change: transform;
          contain: layout style paint;
        }
        
        /* Accrued row - cyan text, italic */
        .dynasty-accrued-row {
          color: #22D3EE !important;
          font-style: italic;
          transform: translateZ(0);
          will-change: transform;
          contain: layout style paint;
        }
        
        .dynasty-season-row:hover {
          background: rgb(64, 64, 64) !important;
        }
        
        .dynasty-rolling-row:hover {
          background: rgb(45, 45, 45) !important;
        }
        
        .dynasty-accrued-row:hover {
          background: rgb(45, 45, 45) !important;
        }
      `}</style>

      {title && (
        <div className={`${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.border.neutral} backdrop-blur-sm text-center ${titlePadding} px-4 mb-2 rounded-t-md border`}>
          <h3 className={`font-bold text-sm ${dynastyTheme.classes.text.primary}`}>
            {title}
          </h3>
        </div>
      )}
      
      <div
        className={`relative rounded-md ${overflowClass}
          shadow-lg`}
        style={{ ...containerStyles, backgroundColor: 'rgb(38, 38, 38)' }}
      >
        <div style={tableStyles}>
          <table ref={tableRef} className="w-full table-fixed text-xs border-collapse" style={tableStyles}>
            <thead className={`${stickyHeader ? 'sticky top-0' : ''} z-20 bg-neutral-800`}>
              <tr className="bg-neutral-800 border-b border-yellow-400/20">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`relative text-center py-0.5 px-1 font-bold text-xs
                      cursor-pointer select-none group
                      transition-colors
                      ${sortConfig?.key === column.key ? 'bg-yellow-400/10' : 'hover:bg-gray-600'}`}
                    style={{ width: getColumnWidth(column.key) }}
                    onClick={() => column.sortable !== false && handleSort(column.key)}
                    onTouchEnd={(e) => {
                      // Support touch events for mobile - only trigger if not clicking resize handle
                      if (e.target.closest('.resize-handle')) return;
                      e.preventDefault();
                      if (column.sortable !== false) {
                        handleSort(column.key);
                      }
                    }}
                  >
                    <div className="flex items-center justify-center relative">
                      <HeaderTooltip title={column.title} columnKey={column.key}>
                        <span className={`truncate transition-colors
                          ${sortConfig?.key === column.key
                            ? dynastyTheme.classes.text.primaryBright
                            : column.titleClass || `${dynastyTheme.classes.text.primary} group-hover:text-yellow-300`}`}>
                          {column.title}
                        </span>
                      </HeaderTooltip>
                      {column.sortable !== false && (
                        <div className="absolute right-0">
                          {getSortIcon(column.key)}
                        </div>
                      )}
                    </div>
                    
                    {/* Resize handle - Wider and more visible */}
                    <div
                      className={`absolute right-0 top-0 bottom-0 w-2 cursor-col-resize 
                        hover:bg-yellow-400/70 transition-colors z-30 group-hover:bg-yellow-400/20`}
                      onMouseDown={(e) => handleMouseDown(e, column.key)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        background: isResizing ? '#D4AF37' : 'transparent'
                      }}
                      title="Drag to resize column"
                    />
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {(() => {
                // =============================================================================
                // STEP 1: Prepare items to render based on table type
                // =============================================================================
                let itemsToRender;
                
                if (tableType === 'roster-lineup') {
                  // Type 1: Generate position slots and match players
                  const slots = generatePositionSlots(leaguePositions?.[playerType]);
                  
                  // Match players to slots based on roster.position
                  slots.forEach(slot => {
                    slot.player = sortedData.find(p => p.league_context?.roster?.position === slot.slotId) || null;
                  });
                  
                  itemsToRender = slots;
                  
                } else if (tableType === 'player-list') {
                  // Type 2: Simple player list - wrap in consistent structure
                  // Preserve totals rows as-is (don't double-wrap)
                  itemsToRender = sortedData.map(item =>
                    item.isTotalsRow ? item : { player: item }
                  );
                  
                } else if (tableType === 'flat-data') {
                  // Type 3: Non-player data (draft picks, ledger, etc.)
                  itemsToRender = sortedData.map(row => ({ flatData: row }));
                }
                
                // =============================================================================
                // STEP 2: Render N rows per item based on rowsPerItem
                // =============================================================================
                return itemsToRender.map((item, itemIndex) => {
                  // Generate rows for this item
                  return rowTypes.map((rowType, rowIndex) => {
                    const key = `${itemIndex}-${rowType}`;

                    // Get custom row className if provided
                    const prevItem = itemIndex > 0 ? itemsToRender[itemIndex - 1] : null;
                    const rowData = tableType === 'flat-data' ? item.flatData : (item.player || item);
                    const prevRowData = prevItem ? (tableType === 'flat-data' ? prevItem.flatData : (prevItem.player || prevItem)) : null;
                    const customRowClass = getRowClassName ? getRowClassName(rowData, itemIndex, prevRowData) : '';
                    // Deep-link anchor (Bible § 4.5.12.2): emit the DOM id only on the item's
                    // FIRST row (rowIndex === 0) so it's unique per item; null/empty → no id.
                    const computedRowId = (rowId && rowIndex === 0) ? rowId(rowData) : null;

                    return (
                      <tr
                        key={key}
                        id={computedRowId || undefined}
                        className={`dynasty-${rowType}-row ${customRowClass}${onRowClick && rowType === 'season' ? ' cursor-pointer' : ''}`}
                        onClick={onRowClick && rowType === 'season' ? () => onRowClick(rowData) : undefined}
                      >
                        {columns.map((column) => {
                          // SIMPLE RULE: Use explicit cellPadding if set, otherwise use defaults
                          const cellPadding = column.cellPadding !== undefined 
                            ? column.cellPadding 
                            : (column.key === 'player' || column.key === 'player_name' || column.allowOverflow)
                              ? 'py-1 px-0'
                              : defaultCellPadding;
                          
                          // Text alignment - respect column override or use smart default
                          const textAlign = column.textAlign || (column.key === 'player_name' || column.allowOverflow ? 'text-left' : 'text-center');
                          
                          // Determine content based on table type
                          let cellContent;
                          
                          if (tableType === 'flat-data') {
                            // Type 3: Non-player data uses standard signature
                            const flatData = item.flatData;
                            cellContent = column.render 
                              ? column.render(getNestedValue(flatData, column.key), flatData, itemIndex)
                              : (getNestedValue(flatData, column.key) ?? '-');
                          } else {
                            // Types 1 & 2: Player data uses new signature
                            // Handle totals rows (no player) and both roster-lineup (double wrap) and player-list (single wrap)
                            const player = item.isTotalsRow ? null : (item.player?.player || item.player || null);
                            cellContent = column.render 
                              ? column.render(player, rowType, item)
                              : (player ? getNestedValue(player, column.key) ?? '-' : '-');
                          }
                          
                          return (
                            <td
                              key={column.key}
                              className={`${cellPadding} ${textAlign} text-xs
                                ${typeof column.className === 'function' ? (
                                  tableType === 'flat-data'
                                    ? column.className(item.flatData, itemIndex)
                                    : column.className(item.isTotalsRow ? null : (item.player?.player || item.player || null), rowType, item)
                                ) : (column.className || '')}`}
                              style={{ width: getColumnWidth(column.key) }}
                            >
                              <div className={column.allowOverflow ? '' : 'truncate'}>
                                {cellContent}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  });
                });
              })()}
              
              {showTotals && totalsRow && (
                <tr className={`font-bold border-t-2 ${dynastyTheme.classes.border.primaryBright} 
                  bg-yellow-400/10 backdrop-blur-sm sticky bottom-0`}>
                  {columns.map((column, colIndex) => {
                    // SIMPLE RULE: Use explicit cellPadding if set, otherwise use defaults
                    const cellPadding = column.cellPadding !== undefined 
                      ? column.cellPadding 
                      : (column.key === 'player' || column.key === 'player_name' || column.allowOverflow)
                        ? 'py-1 px-0'
                        : defaultCellPadding;
                    
                    // Text alignment - respect column override or use smart default
                    const textAlign = column.textAlign || (column.key === 'player_name' || column.allowOverflow ? 'text-left' : 'text-center');
                    
                    return (
                    <td
                      key={column.key}
                      className={`${cellPadding} ${textAlign} text-xs
                        ${dynastyTheme.classes.text.primary}`}
                      style={{ width: getColumnWidth(column.key) }}
                    >
                      <div className="truncate">
                        {column.render ? column.render(totalsRow, null, null) : (getNestedValue(totalsRow, column.key) ?? '-')}
                      </div>
                    </td>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {sortedData.length === 0 && (
          <div className={`text-center py-12 ${dynastyTheme.classes.bg.darkFlat}`}>
            <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>No data available</p>
            <p className={`text-xs mt-2 ${dynastyTheme.classes.text.neutral}`}>Check back later for updates</p>
          </div>
        )}
      </div>

    </div>
  );
};